import { useState, useRef } from 'react';
import request from '../../utils/request';
import './CreateFunctionModal.css';

function isInsideModalContent(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('.modal-content') !== null;
}

interface CreateFunctionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateFunctionRequest {
  name: string;
  code: string;
  sort: number;
}

export default function CreateFunctionModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateFunctionModalProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [sort, setSort] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter function name');
      return;
    }
    if (!code.trim()) {
      setError('Please enter function code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const data: CreateFunctionRequest = {
        name: name.trim(),
        code: code.trim(),
        sort: sort,
      };

      await request.post('/gradmin/functions', data);

      // Reset form
      setName('');
      setCode('');
      setSort(0);

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setCode('');
      setSort(0);
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={e => {
        mouseDownInsideContent.current = isInsideModalContent(e.target);
      }}
      onClick={e => {
        if (e.target === overlayRef.current && !mouseDownInsideContent.current) {
          handleClose();
        }
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Add Function</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error-alert">{error}</div>}

            <div className="modal-form-group">
              <label htmlFor="function-name" className="modal-form-label">
                Function Name <span className="modal-required">*</span>
              </label>
              <input
                id="function-name"
                type="text"
                className="modal-form-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter function name"
                disabled={loading}
              />
            </div>

            <div className="modal-form-group">
              <label htmlFor="function-code" className="modal-form-label">
                Function Code <span className="modal-required">*</span>
              </label>
              <input
                id="function-code"
                type="text"
                className="modal-form-input"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="Enter function code"
                disabled={loading}
              />
              <span className="modal-form-hint">
                Function code is used for internal system identification and cannot be modified
                after creation
              </span>
            </div>

            <div className="modal-form-group">
              <label htmlFor="function-sort" className="modal-form-label">
                Sort
              </label>
              <input
                id="function-sort"
                type="number"
                className="modal-form-input"
                value={sort}
                onChange={e => setSort(Number(e.target.value))}
                placeholder="Enter sort value (smaller = higher priority)"
                disabled={loading}
              />
              <span className="modal-form-hint">
                Smaller value means higher priority. Default is 0.
              </span>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="modal-btn modal-btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="modal-btn modal-btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Confirm Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
